import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './WarningModal.styles';
import { Modal } from '@/components/UI/Modal';
import { Button } from '@/components/UI/Button/Button';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { ButtonAppearance } from '@/components/UI/Button/Button.types';

type ActionButtonProps = {
  text: string;
  appearance?: ButtonAppearance;
  disabled?: boolean;
  onClick: () => void | Promise<void>;
};

export interface WarningModalProps {
  isOpen: boolean;
  onClose: () => void | Promise<void>;
  title: string;
  description: string;
  icon?: string;
  iconSize?: number;
  buttonText?: string;
  dataTestId?: string;
  actions?: {
    left?: ActionButtonProps;
    right?: ActionButtonProps;
  };
}

export const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  icon,
  iconSize = 35,
  buttonText,
  dataTestId,
  actions,
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={isDesktop ? 445 : 345}
    >
      <S.Content>
        {icon && (
          <S.Icon>
            <IconBox
              $filename={icon}
              $width={iconSize}
              $height={iconSize}
              $iconWidth={iconSize}
              $iconHeight={iconSize}
            />
          </S.Icon>
        )}
        <S.Title>{title}</S.Title>
        <S.Description>{description}</S.Description>
        {actions ? (
          <S.ActionsContainer>
            {actions.left && (
              <Button
                appearance={actions.left.appearance ?? 'secondaryOutlined'}
                isFullWidth
                disabled={actions.left.disabled}
                borderRadius={14}
                onClick={actions.left.onClick}
              >
                {actions.left.text}
              </Button>
            )}
            {actions.right && (
              <Button
                appearance={actions.right.appearance ?? 'secondary'}
                isFullWidth
                disabled={actions.right.disabled}
                borderRadius={14}
                onClick={actions.right.onClick}
              >
                {actions.right.text}
              </Button>
            )}
          </S.ActionsContainer>
        ) : (
          <Button
            appearance="secondary"
            isFullWidth
            borderRadius={14}
            onClick={onClose}
          >
            {buttonText ?? t('common.got_it')}
          </Button>
        )}
      </S.Content>
    </Modal>
  );
};
